import React, { memo, useEffect, useState, useCallback } from "react";
import { compose } from "redux";
import { reduxForm } from "redux-form";
import { connect, useDispatch } from "react-redux";
import { createStructuredSelector } from "reselect";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import join from "lodash/join";
import isFunction from "lodash/isFunction";
import validate from "validations";
import { forms, SELF_ACT_DEL, SELF_ACTIVATION_SIM } from "utils/constants";
import SmartRender from "containers/SmartRender";
import Spinner, { styles } from "components/Spinner";
import OTPSubmitPage from "containers/VerifyOTP/OTPSubmitPage";
import {
  fetchMyInfoOTPToken,
  verifyMyInfoOTP,
  createInitialOrder,
  disableResendActivation
} from "ducks/actions";

import {
  makeSelectPageValidations,
  makeSelectIsFetchingAnyData,
  makeSelectThemeColors,
  makeSelectPersonalDetailsForm,
  makeSelectMyInfoOTPToken,
  makeSelectMyInfoLogId,
  makeSelectOrderParams,
  makeSelectSadOrderParams,
  makeSelectValidateIccId,
  makeSelectPageData,
  makeSelectOrderRef,
  makeSelectIsSimTypeEsim
} from "ducks/selectors";

import { getChannelFromUrl, gotoUrl } from "utils/helpers";

const ChannelOTP = Component =>
  function Comp(props) {
    const {
      isFetchingAnyData,
      personalDetails,
      myInfoOTPToken,
      myInfoLogId,
      messageData,
      orderParams,
      sadOrderParams,
      validateIccid,
      pageData,
      orderRef
    } = props;

    const [enableNextPage, setEnableNextPage] = useState(null);
    const [shouldCreateInitialOrder, setShouldCreateInitialOrder] = useState(
      false
    );
    const [OTPSize, setOTPSize] = useState(6);
    const dispatch = useDispatch();

    // eslint-disable-next-line
    const fetchMyInfoOTPRequest = useCallback(() => {
      dispatch(
        fetchMyInfoOTPToken(
          {
            myinfo_log_id: myInfoLogId,
            country_code: parseInt(
              get(personalDetails, "country_code")
            ).toString(),
            phone_number: get(personalDetails, "contact_number")
          },
          result => {
            if (result) dispatch(disableResendActivation());
          }
        )
      );
    });

    const submitVerifyMyInfoOTP = (navigate, payload) => {
      const otp = join(get(payload, "current_otp", []), "");
      otp &&
        dispatch(
          verifyMyInfoOTP(
            {
              myinfo_log_id: myInfoLogId,
              country_code: parseInt(
                get(personalDetails, "country_code")
              ).toString(),
              phone_number: get(personalDetails, "contact_number"),
              otp,
              otp_token: myInfoOTPToken
            },
            result => {
              if (get(result, "is_otp_valid")) {
                if (getChannelFromUrl() === SELF_ACTIVATION_SIM) {
                  gotoUrl(`/${getChannelFromUrl()}/order-summary`, false, {
                    order_ref: get(sadOrderParams, "order_id") || orderRef
                  });
                } else {
                  navigate();
                }
              }
            }
          )
        );
    };

    const listenToValidInputChange = (
      boxCount,
      isValidInput,
      otp,
      navigate,
      autoSubmit
    ) => {
      setEnableNextPage(isValidInput);
      setOTPSize(boxCount);
      if (autoSubmit && isValidInput && !isEmpty(otp) && isFunction(navigate)) {
        // show the otp for 0.5 seconds before auto submit
        setTimeout(() => {
          submitVerifyMyInfoOTP(navigate, { current_otp: otp });
        }, 500);
      }
    };

    const createInitialOrderRequest = (
      orderParams,
      validateIccid,
      orderRef
    ) => {
      const isSadFlow = get(validateIccid, "channel") === SELF_ACT_DEL;
      if (!isSadFlow && !isEmpty(orderParams) && !orderRef) {
        dispatch(createInitialOrder(orderParams));
      }
    };

    const updatedProps = {
      ...props,
      fetchMyInfoOTPToken: fetchMyInfoOTPRequest,
      listenToValidInputChange,
      nextPage: enableNextPage,
      submitVerifyMyInfoOTP,
      timerKey: myInfoOTPToken,
      messageData: {
        ...messageData,
        otpSize: OTPSize,
        otpSentToNumber: get(personalDetails, "contact_number", "")
      }
    };

    useEffect(() => {
      fetchMyInfoOTPRequest();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setShouldCreateInitialOrder(get(pageData, "createInitialOrder"));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageData.createInitialOrder]);

    useEffect(
      () => {
        if (shouldCreateInitialOrder)
          createInitialOrderRequest(orderParams, validateIccid, orderRef);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [shouldCreateInitialOrder]
    );

    if (isFetchingAnyData) {
      return <Spinner size={200} sx={styles.fullPage} />;
    }
    return <OTPSubmitPage {...updatedProps} />;
  };

const mapStateToProps = createStructuredSelector({
  formValidations: makeSelectPageValidations(forms.CHANNEL_OTP_FORM),
  themeColors: makeSelectThemeColors(),
  isFetchingAnyData: makeSelectIsFetchingAnyData(),
  personalDetails: makeSelectPersonalDetailsForm(),
  myInfoOTPToken: makeSelectMyInfoOTPToken(),
  myInfoLogId: makeSelectMyInfoLogId(),
  orderParams: makeSelectOrderParams(),
  sadOrderParams: makeSelectSadOrderParams(),
  validateIccid: makeSelectValidateIccId(),
  pageData: makeSelectPageData(),
  orderRef: makeSelectOrderRef(),
  isSimTypeEsim: makeSelectIsSimTypeEsim()
});
const mapDispatchToProps = dispatch => {
  return {
    verifyMyInfoOTP: (params, callback) => {
      dispatch(verifyMyInfoOTP(params, callback));
    }
  };
};
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
  reduxForm({
    form: forms.CHANNEL_OTP_FORM,
    destroyOnUnmount: false,
    validate
  }),
  memo
)(ChannelOTP(SmartRender));
